import { useCallback, useRef } from 'react';

import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';

import { type ErrorData } from '@/app/types/yandex';

import { useUser } from '@/entities/user/model/user.store';

import { CustomErrorCode } from './types';

// ID из документации
// https://fantasyinvest.flowfast.io/space/79846/boards/card/6598586
const SDK_FUNCTION_NAME = 'show_10087681' as const;
const SDK_SCRIPT_URL = '//munqu.com/sdk.js';
const SDK_ZONE_ID = '10087681';

interface MoneytagAdsProps {
  onReward?: () => void;
  onError?: (error: ErrorData) => void;
  ymid?: string | null;
}

const makeErrorData = (message: string, code = 'CUSTOM_ERROR'): ErrorData => {
  return {
    type: 'error',
    code,
    text: message,
  };
};

// Функция для загрузки скрипта
const loadMoneytagScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window[SDK_FUNCTION_NAME]) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = SDK_SCRIPT_URL;
    script.dataset.zone = SDK_ZONE_ID;
    script.dataset.sdk = SDK_FUNCTION_NAME;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Ushoaglosee script'));

    document.head.appendChild(script);
  });
};

export const useMoneytagAds = ({ onReward, onError, ymid }: MoneytagAdsProps = {}) => {
  const { t } = useTranslation();
  const user = useUser();
  const scriptLoadedRef = useRef(false);
  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  const handleError = useCallback(
    (err: ErrorData) => {
      console.error(err.text, err.code);
      onError?.(err);
    },
    [onError]
  );

  const show = useCallback(async () => {
    const handleStart = () => {
      backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;
      if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();
    };

    const handleEnd = () => {
      if (backButtonWasVisibleRef.current) {
        WebApp.BackButton.show();
        backButtonWasVisibleRef.current = true;
      }
    };

    try {
      if (!scriptLoadedRef.current) {
        await loadMoneytagScript();
        scriptLoadedRef.current = true;
      }

      if (!window[SDK_FUNCTION_NAME as keyof Window]) {
        return handleError(makeErrorData('Failed to load ad script'));
      }

      if (!user?.telegramId) {
        return handleError(makeErrorData('Telegram ID is required'));
      }

      handleStart();

      try {
        await window[SDK_FUNCTION_NAME as keyof Window]({
          ymid,
        });
        onReward?.();
      } catch (error) {
        handleError(
          makeErrorData(
            t('Unfortunately, there is no available ad at the moment'),
            CustomErrorCode.NO_AVAILABLE_ADS
          )
        );
      }
    } catch (error) {
      handleError(
        makeErrorData(
          t('An error occurred while showing the ad'),
          CustomErrorCode.GENERAL_ERROR
        )
      );
    } finally {
      handleEnd();
    }
  }, [handleError, onReward, t, user?.telegramId]);

  return show;
};
